import { Component, ViewChild, Output, EventEmitter, OnInit } from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { ReviewService } from '../../../services/review.service';
import { Constants } from '../../../constants/constants';
import { Subject, takeUntil } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { ExportTablesToPDFOptions } from '../../../models/ExportTableToPDF';
import { LaborHoursDistribution } from '../../../models/LaborHoursDistribution';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';

@Component({
  selector: 'app-sol-overtime-prevailing-wage',
  imports: [CommonModule, MatSortModule, MatPaginatorModule, MatTableModule, TranslatePipe],
  providers: [DecimalPrecisionPipe],
  templateUrl: './sol-overtime-prevailing-wage.component.html',
  styleUrls: ['./sol-overtime-prevailing-wage.component.scss']
})
export class SolOvertimePrevailingWageComponent implements OnInit {

  // Displayed columns for the table -----------------------------------------------------------------------------------------------
  columns: { field: string, header: string, isRightAlign: boolean, colWidth: string }[] = [
    { field: 'levelID', header: 'Level ID', isRightAlign: false, colWidth: '100px' },
    { field: 'levelDescription', header: 'Level Description', isRightAlign: false, colWidth: '200px' },
    { field: 'description', header: '', isRightAlign: false, colWidth: '300px' },
    { field: 'pmHours', header: 'REVIEW_SOL_OT_PWR_TAB.PM', isRightAlign: true, colWidth: '100px' },
    { field: 'engHours', header: 'REVIEW_SOL_OT_PWR_TAB.ENG', isRightAlign: true, colWidth: '100px' },{ field: 'cengHours', header: 'REVIEW_SOL_HOURS_TASKS_TAB.CENG', isRightAlign: true, colWidth: 'auto' },
    { field: 'eengHours', header: 'REVIEW_SOL_HOURS_TASKS_TAB.EENG', isRightAlign: true, colWidth: '100px' },
    { field: 'lengHours', header: 'REVIEW_SOL_HOURS_TASKS_TAB.LENG', isRightAlign: true, colWidth: '100px' },
    { field: 'nengHours', header: 'REVIEW_SOL_HOURS_TASKS_TAB.NENG', isRightAlign: true, colWidth: '100px' },
    { field: 'qengHours', header: 'REVIEW_SOL_HOURS_TASKS_TAB.QENG', isRightAlign: true, colWidth: '100px' },
    { field: 'grapHours', header: 'REVIEW_SOL_OT_PWR_TAB.GRAP', isRightAlign: true, colWidth: '100px' },
    { field: 'progHours', header: 'REVIEW_SOL_OT_PWR_TAB.PROG', isRightAlign: true, colWidth: '100px' },
    { field: 'specHours', header: 'REVIEW_SOL_OT_PWR_TAB.SPEC', isRightAlign: true, colWidth: '100px' },
    { field: 'ddsHours', header: 'REVIEW_SOL_OT_PWR_TAB.DDS', isRightAlign: true, colWidth: '100px' },
    { field: 'mechHours', header: 'REVIEW_SOL_OT_PWR_TAB.MECH', isRightAlign: true, colWidth: '100px' },
    { field: 'elecHours', header: 'REVIEW_SOL_OT_PWR_TAB.ELEC', isRightAlign: true, colWidth: '100px' },
    { field: 'typicalOfCount', header: 'REVIEW_SOL_OT_PWR_TAB.TYPICAL_OF_COUNT', isRightAlign: false, colWidth: '100px' }
  ];

  displayedColumnsOvertimeWise: string[] = [
    'description',
    'pmHours',
    'engHours',
    'cengHours',
    'eengHours',
    'lengHours',
    'nengHours',
    'qengHours',
    'grapHours',
    'progHours',
    'specHours',
    'ddsHours',
    'mechHours',
    'elecHours',
  ];
  descriptionColumnList: string[] = [];
  readonly hourFields: (keyof LaborHoursDistribution)[] = [
    'pmHours', 'engHours', 'cengHours', 'eengHours', 'lengHours',
    'nengHours', 'qengHours', 'grapHours', 'progHours', 'specHours',
    'ddsHours', 'mechHours', 'elecHours'
  ];

  overtimeDataList: LaborHoursDistribution[] = [];
  selectedRow: { [key: string]: LaborHoursDistribution } = {};
  dataSourceOvertime = new MatTableDataSource<LaborHoursDistribution>(this.overtimeDataList);
  dataSourceMap: { [key: string]: MatTableDataSource<LaborHoursDistribution> } = {};
  dynamicColumns: string[] = [];
  groupedReviewList: LaborHoursDistribution[][] = [];
  sums: any[] = [];
  @ViewChild(MatSort) sort: MatSort | null = null;
  laborHoursDistributionDetails: LaborHoursDistribution[] = new Array<LaborHoursDistribution>();
  checkDataExists: boolean = false;
  destroy$ = new Subject<void>();
  @Output() disablePdf = new EventEmitter<boolean>();

  constructor(private readonly commonFunctions: CommonFunctionsService,
    private readonly reviewService: ReviewService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe,
    private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService) { }

  ngOnInit() {
    this.initializeLaborHoursDistributionDetails();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf();
      });
    this.sendDisablePdfEvent();
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete()
  }

  initializeLaborHoursDistributionDetails() {
    const newCostReviewObject = this.reviewService.newCostReviewObject;
    if (newCostReviewObject?.laborHoursDistributionDetails) {
      this.laborHoursDistributionDetails = newCostReviewObject.laborHoursDistributionDetails;
      if (this.laborHoursDistributionDetails.length > 0) {
        this.checkDataExists = true;
        this.getAllMappedOvertimeWise();
        this.groupedReviewList = this.groupByLevelID(this.overtimeDataList);
        this.addSumsToGroups();
      }
    }
  }

  getAllMappedOvertimeWise() {
    const mappedData = JSON.parse(JSON.stringify(this.laborHoursDistributionDetails || [])).map((detail: LaborHoursDistribution) => {
      const formatted: any = { ...detail };
      this.hourFields.forEach(field => {
        formatted[field] = this.commonFunctions.formatToTwoDecimal(detail[field]);
      });
      return formatted;
    });
    const hasNonZeroHours = (detail: LaborHoursDistribution) =>
      this.hourFields.some(field => parseFloat(String(detail[field])) !== Constants.ZERO_HOURS);
    const filteredData = mappedData.filter(hasNonZeroHours);
    this.checkDataExists = filteredData.length !== Constants.ZERO;
    this.overtimeDataList = this.checkDataExists ? mappedData : filteredData;
  }

  groupByLevelID(list: LaborHoursDistribution[]): LaborHoursDistribution[][] {
    const groups: { [key: string]: LaborHoursDistribution[] } = {};
    list.forEach(item => {
      if (!groups[item.levelID]) {
        groups[item.levelID] = [];
      }
      groups[item.levelID].push(item);
    });
    Object.values(groups).forEach((group: LaborHoursDistribution[]) => {
      this.descriptionColumnList.push(group[0].levelDescription);
    });
    return Object.values(groups);
  }

  addSumsToGroups() {
    this.groupedReviewList.forEach((group: LaborHoursDistribution[]) => {
      const sum: any = { description: 'Total Hours' };
      this.hourFields.forEach(column => {
        sum[column] = this.decimalPrecisionPipe.transform(group.reduce((acc: any, item: any) => acc + (parseFloat(item[column]) || 0), 0));
      });
      group.push(sum);
    });
  }

  setSelectedRow(row: LaborHoursDistribution, levelId: string) {
    this.selectedRow[levelId] = row;
  }

  getDynamicHeaders(levelDescription: string): { [key: string]: string } {
    return {
      levelID: levelDescription,
      levelDescription: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.LEVEL_DESCRIPTION'),
      description: levelDescription,
      pmHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.PM'),
      engHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.ENG'),
      cengHours: this.translate.instant('REVIEW_SOL_HOURS_TASKS_TAB.CENG'),
      eengHours: this.translate.instant('REVIEW_SOL_HOURS_TASKS_TAB.EENG'),
      lengHours: this.translate.instant('REVIEW_SOL_HOURS_TASKS_TAB.LENG'),
      nengHours: this.translate.instant('REVIEW_SOL_HOURS_TASKS_TAB.NENG'),
      qengHours: this.translate.instant('REVIEW_SOL_HOURS_TASKS_TAB.QENG'),
      grapHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.GRAP'),
      progHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.PROG'),
      specHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.SPEC'),
      ddsHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.DDS'),
      mechHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.MECH'),
      elecHours: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.ELEC'),
      typicalOfCount: this.translate.instant('REVIEW_SOL_OT_PWR_TAB.TYPICAL_OF_COUNT')
    };
  }

  exportToPdf() {
    if (Constants.SOL_OVERTIME != 'OT/PWR') { 
      return;
    }
    if (!this.checkDataExists || this.groupedReviewList.length === Constants.ZERO) {
      const options: ExportObjectAsFieldValuePDFOptions = {
        data: { [this.translate.instant('REVIEW_SOL_OT_PWR_TAB.OT_PWR_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
        title: Constants.EMPTY_STRING,
        fileName: 'overtime-prevailing-wage.pdf',
        estimateID: this.reviewService.selectedReview.estimateID,
        estimateName: this.reviewService.selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.OT_PWR'),
      };
      this.commonFunctions.exportObjectAsFieldValuePDF(options);
      return;
    }

    const tables = this.groupedReviewList.map((group, idx) => {
      const levelDescription = group[0]?.levelDescription || `Level ${group[0]?.levelID || idx + 1}`;
      return {
        title: `${levelDescription}`,
        displayedColumns: this.displayedColumnsOvertimeWise,
        headers: this.getDynamicHeaders(levelDescription),
        data: group,
        typeGuard: (row: LaborHoursDistribution, col: string) => row[col as keyof LaborHoursDistribution]
      };
    });

    const options: ExportTablesToPDFOptions = {
      tables,
      fileName: 'overtime-prevailing-wage.pdf',
      hasNotes: false,
      estimateID: this.reviewService.selectedReview.estimateID,
      estimateName: this.reviewService.selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.OT_PWR')
    };
    this.commonFunctions.exportTablesToPDF(options);
  }

}
